// src/redux/slices/lessonsSlice.ts

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getLessonDetailsApi,
  LessonDetails,
  markLessonCompletedApi,
} from "../../apis/lessonsApis";

/* =====================================================
   STATE TYPE
===================================================== */

interface LessonsState {
  lessonDetails: LessonDetails | null;
  loading: boolean;
  error: string | null;
  markCompletedLoading: boolean;
  markCompletedMessage: string | null;
}

const initialState: LessonsState = {
  lessonDetails: null,
  loading: false,
  error: null,
  markCompletedLoading: false,
  markCompletedMessage: null,
};

/* =====================================================
   THUNK
===================================================== */

export const fetchLessonDetails = createAsyncThunk(
  "lessons/fetchLessonDetails",
  async (lessonId: string, { rejectWithValue }) => {
    try {
      const response = await getLessonDetailsApi(lessonId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.data || "Failed to fetch lesson details",
      );
    }
  },
);

export const markLessonCompleted = createAsyncThunk(
  "lessons/markLessonCompleted",
  async (
    { lessonId, isCompleted }: { lessonId: string; isCompleted: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await markLessonCompletedApi(lessonId, {
        isCompleted,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update lesson status",
      );
    }
  },
);

/* =====================================================
   SLICE
===================================================== */

const lessonsSlice = createSlice({
  name: "lessons",
  initialState,
  reducers: {
    clearLessonDetails: (state) => {
      state.lessonDetails = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLessonDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLessonDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.lessonDetails = action.payload;
      })
      .addCase(fetchLessonDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(markLessonCompleted.pending, (state) => {
        state.markCompletedLoading = true;
        state.markCompletedMessage = null;
      })
      .addCase(markLessonCompleted.fulfilled, (state, action) => {
        state.markCompletedLoading = false;
        state.markCompletedMessage = action.payload;

        // optionally update local lessonDetails if needed
        if (state.lessonDetails) {
          // You can add isCompleted in LessonDetails type if backend returns it
          // state.lessonDetails.isCompleted = true;
        }
      })
      .addCase(markLessonCompleted.rejected, (state, action) => {
        state.markCompletedLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearLessonDetails } = lessonsSlice.actions;
export default lessonsSlice.reducer;
